import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  PARTNER_DEAL_STAGE_LABELS,
  PartnerDealResponse,
  PartnerDealStage,
} from '@interfaces/partners/responses/partner-deal.response';
import { TranslateService } from '@ngx-translate/core';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';

/** O que a modal devolve ao board: o deal, a etapa de destino e o motivo digitado. */
export interface MoveBackStageConfirmed {
  deal: PartnerDealResponse;
  stage: PartnerDealStage;
  position: number;
  reason: string;
}

/**
 * Pede o motivo quando o time interno volta um deal de etapa no board (card 3579).
 *
 * O parceiro recebe um e-mail dizendo que o deal voltou e **por quê** (resposta de 20/10/2026 na página do
 * card no acervo), então o motivo é obrigatório aqui. A modal só coleta o texto: quem grava é o board, com o
 * mesmo PATCH de etapa do arraste, e é ele que devolve o card se a pessoa cancelar.
 */
@Component({
  selector: 'app-modal-move-back-stage',
  templateUrl: './modal-move-back-stage.component.html',
  styleUrls: ['./modal-move-back-stage.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
})
export class ModalMoveBackStageComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** A pessoa confirmou a volta com o motivo. */
  @Output() confirmed = new EventEmitter<MoveBackStageConfirmed>();

  /** A pessoa fechou sem confirmar: o board desfaz o movimento. */
  @Output() cancelled = new EventEmitter<void>();

  deal: PartnerDealResponse | null = null;
  fromLabel = '';
  toLabel = '';

  private stage: PartnerDealStage | null = null;
  private position = 1;
  private submitted = false;

  form: FormGroup = this.formBuilder.group({
    reason: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(2000)]],
  });

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  /** Abre a modal para o deal que foi solto numa etapa anterior. */
  open(
    deal: PartnerDealResponse,
    from: PartnerDealStage,
    to: PartnerDealStage,
    position: number
  ): void {
    this.deal = deal;
    this.stage = to;
    this.position = position;
    this.fromLabel = PARTNER_DEAL_STAGE_LABELS[from];
    this.toLabel = PARTNER_DEAL_STAGE_LABELS[to];
    this.submitted = false;
    this.form.reset({ reason: null });

    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'move-back-stage', false, true);
  }

  isFormValid(): boolean {
    return this.form.valid && !!this.deal && !!this.stage;
  }

  submitAction(): void {
    if (!this.isFormValid()) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitted = true;
    this.confirmed.emit({
      deal: this.deal!,
      stage: this.stage!,
      position: this.position,
      reason: String(this.form.getRawValue().reason).trim(),
    });
    this.dialogRef?.close();
  }

  /** Fechar pelo X, pelo Cancelar ou pelo Esc é desistir da volta. */
  onClose(): void {
    if (!this.submitted) this.cancelled.emit();
  }
}
